/**
 * Demo flag — gates the prospect-demo MSW bundle.
 *
 * Independent of `apiEnv=mock` (which is dev-only via #hash). The demo flag is
 * meant to ship on the sales environment so a sales rep can hit
 *   https://lender-sales.sharegain.com/sg-lender/home?demo=american
 * and get a fully self-contained prospect demo. Without the flag, the sales
 * build behaves normally and hits the real backend.
 *
 * Resolution order: `?demo=<name>` URL param (sticky — persisted to
 * sessionStorage on first hit) → `sgDemo` sessionStorage key → null.
 *
 * Persistence note: the activation key lives in **sessionStorage**, not
 * localStorage. A reload within the same tab/session keeps the demo running
 * (so a rep mid-demo isn't dropped), but closing the tab/browser clears it.
 * This protects the shared/kiosk sales-machine case: the next visitor who
 * opens the real sales site with no `?demo=` does NOT silently inherit a
 * lingering demo + auth-bypass. The `?demo=` URL param still re-activates and
 * re-sticks within the new session. (Caveat: "Duplicate tab" / open-in-new-tab
 * clones sessionStorage, so a duplicated demo tab inherits demo mode even after
 * the `?demo=` param was stripped — far narrower than the old localStorage leak
 * and it never affects a fresh visitor, but it's not "airtight per tab".)
 * Other demo keys (scenario-store `mock-config`, per-demo `:seeded` markers,
 * skin overrides/ad-hoc skins) deliberately stay in localStorage — they are
 * demo *content*, harmlessly inert without an active activation key.
 *
 * Every reader of this key MUST read sessionStorage. Direct readers that
 * cannot import this module (one-way deps) — recent-searches.service.ts and
 * hidden-pages-conditions.ts — read `sessionStorage.getItem('sgDemo')`
 * directly. Keep them in sync with STORAGE_KEY below.
 */

import type { DemoBase, DemoCountryCode, DemoSkin } from './demo-skins';
import { DEMO_BASES, getSkin, listSkinNames, setAdhocSkin } from './demo-skins';

/**
 * The activation key, stored in sessionStorage (NOT localStorage — see the
 * module doc comment). If you change this key, also update the two direct
 * readers in libs that can't import this module:
 *   - libs/common/instrument-search/src/lib/recent-searches.service.ts
 *   - libs/apps/lender/src/lib/helpers/hidden-pages-conditions.ts
 */
const STORAGE_KEY = 'sgDemo';
const URL_PARAM = 'demo';
const URL_BASE_PARAM = 'base';
/** Re-uses the registry's own list — this used to be a second hand-maintained
 *  copy of the `DemoBase` union, so a base added to one and not the other made
 *  `?base=<new>` silently unrecognised. */
const VALID_BASES: readonly DemoBase[] = DEMO_BASES;
/** Remembers the apiEnv the user was on before entering demo mode, so
 *  exit-demo can restore it instead of nuking apiEnv entirely. */
const PRE_DEMO_API_ENV_KEY = 'sgPreDemoApiEnv';

/** Names recognised as valid prospect demos. Sourced from the skin registry —
 *  new prospects appear here automatically once added to DEMO_SKINS. Read on
 *  module load; ad-hoc skins added at runtime require a page reload to surface
 *  here (which the editor popup forces on save anyway). */
export const KNOWN_DEMOS = listSkinNames();
/** A demo name is just a string now (skin names are dynamic). Kept as a type
 *  alias for back-compat with handlers that imported it. */
export type DemoName = string;

function parseFromUrl(): string | null {
  try {
    return new URL(location.href).searchParams.get(URL_PARAM);
  } catch {
    return null;
  }
}

function parseBaseFromUrl(): DemoBase | null {
  try {
    const raw = new URL(location.href).searchParams.get(URL_BASE_PARAM);
    return VALID_BASES.includes(raw as DemoBase) ? (raw as DemoBase) : null;
  } catch {
    return null;
  }
}

function isKnown(name: string | null): name is DemoName {
  // Re-read the skin list dynamically so ad-hoc skins registered earlier in
  // this same page load (e.g. from `?demo=newco&base=american`) resolve too.
  return !!name && listSkinNames().includes(name);
}

/**
 * Bootstrap an ad-hoc skin from `?demo=<name>` when <name> isn't in
 * DEMO_SKINS. `?base=<base>` is optional — defaults to `american` (US/USD/
 * super-lender, the more feature-rich template) so a bare `?demo=eeee`
 * activates a demo immediately. Identity is seeded with placeholders the
 * popup will overwrite. Returns true if a new ad-hoc skin was created.
 */
/**
 * Default country per base for an ad-hoc skin. A `Record<DemoBase, …>` rather
 * than the nested ternary this replaced: that ternary fell through to 'US' for
 * any base it didn't name, so a new base silently got a US flag until someone
 * noticed on stage. Now a missing base fails the build.
 */
const BASE_DEFAULT_COUNTRY: Record<DemoBase, DemoCountryCode> = {
  american: 'US',
  greek: 'GR',
  turkish: 'TR',
  swiss: 'CH',
  uk: 'GB',
  lux: 'LU',
  rbc: 'CA',
};

function tryCreateAdhocFromUrl(name: string): boolean {
  const base: DemoBase = parseBaseFromUrl() ?? 'american';
  setAdhocSkin({
    name,
    base,
    identity: {
      companyName: titleCase(name),
      // `code` is intentionally omitted — getCode() derives it from
      // companyName so editing the company in the popup auto-updates the
      // watermark pill (an explicit code here would freeze the URL slug).
      countryCode: BASE_DEFAULT_COUNTRY[base],
      user: { firstName: '', lastName: '' },
    },
  });
  return true;
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Resolve the active demo's full skin object (identity + base). Source of
 * truth for any handler that needs to read the customer-facing company name,
 * user name, entity label, code, country, etc.
 */
export function getActiveSkin(): DemoSkin | null {
  return getSkin(getActiveDemo());
}

/**
 * The data template the active skin layers on — picks tickers, currency,
 * hierarchy, feature flags. Handlers that branch on "which data factory to
 * call" should use this; handlers that surface identity strings should use
 * getActiveSkin() instead.
 */
export function getActiveBase(): DemoBase | null {
  return getActiveSkin()?.base ?? null;
}

/**
 * Returns the active demo name (e.g. 'american'), or null if no demo is active.
 * Side-effect: persists the URL value to sessionStorage so a reload within the
 * same tab keeps the demo (but closing the tab/browser clears it).
 */
export function getActiveDemo(): DemoName | null {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined' || typeof localStorage === 'undefined') {
    return null;
  }
  const fromUrl = parseFromUrl();
  if (fromUrl) {
    // Unknown skin + `?base=<base>` → register an ad-hoc skin so the rest of
    // the boot (handlers, seeder) sees it. The popup auto-opens after to let
    // the rep fill in the identity fields.
    if (!isKnown(fromUrl) && tryCreateAdhocFromUrl(fromUrl)) {
      // Fall through — isKnown now resolves it (listSkinNames is dynamic).
    }
    if (isKnown(fromUrl)) {
      // Capture the user's pre-demo apiEnv on first activation (helper is
      // idempotent — only writes once, so re-entering or refreshing doesn't
      // clobber the original env we want to restore on exit-demo).
      savePreDemoApiEnv(localStorage.getItem('apiEnv'));
      // Activation key is session-scoped: re-sticks for this tab's session
      // only, so it doesn't leak to the next visitor on a shared machine.
      sessionStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
  }
  const stored = sessionStorage.getItem(STORAGE_KEY);
  return isKnown(stored) ? stored : null;
}

/** True when any prospect demo is active. */
export function isDemoMode(): boolean {
  return getActiveDemo() !== null;
}

/**
 * Save the apiEnv the user was on before entering demo mode so exit-demo can
 * restore it. No-op if there's nothing to remember (apiEnv unset, or already a
 * pseudo-env), or if a value is already saved (don't overwrite the original
 * pre-demo env when switching between prospects).
 */
export function savePreDemoApiEnv(currentApiEnv: string | null): void {
  if (typeof localStorage === 'undefined') return;
  if (!currentApiEnv || currentApiEnv === 'mock' || currentApiEnv === 'demo') return;
  if (localStorage.getItem(PRE_DEMO_API_ENV_KEY)) return;
  localStorage.setItem(PRE_DEMO_API_ENV_KEY, currentApiEnv);
}

/**
 * Restore the saved pre-demo apiEnv (if any) onto `apiEnv`. If nothing was
 * saved, clear `apiEnv` only when it's a pseudo-env (mock/demo) so the user
 * doesn't end up still in MSW mode after exit-demo.
 */
export function restorePreDemoApiEnv(): void {
  if (typeof localStorage === 'undefined') return;
  const preDemo = localStorage.getItem(PRE_DEMO_API_ENV_KEY);
  if (preDemo) {
    localStorage.setItem('apiEnv', preDemo);
    localStorage.removeItem(PRE_DEMO_API_ENV_KEY);
    return;
  }
  const apiEnv = localStorage.getItem('apiEnv');
  if (apiEnv === 'mock' || apiEnv === 'demo') {
    localStorage.removeItem('apiEnv');
  }
}

/**
 * Switch the active demo. Wipes the scenario-store snapshot and every demo's
 * "already seeded" flag so the new demo's seedDemoScenarios re-runs cleanly on
 * the next page load — otherwise stale presets from the previous demo bleed
 * through (sections share IDs between demos).
 *
 * Caller is responsible for triggering a page reload after this returns.
 */
export function setActiveDemo(name: DemoName): void {
  if (typeof localStorage === 'undefined' || typeof sessionStorage === 'undefined') return;
  // Activation key is session-scoped (sessionStorage); the scenario-store
  // snapshot + per-demo seed markers stay in localStorage (demo content).
  sessionStorage.setItem(STORAGE_KEY, name);
  localStorage.removeItem('mock-config');
  // Drop every "already seeded" marker — both the legacy unversioned key and
  // any `sgDemo:<demo>:seeded:vN` keys — so the new demo's seeder always runs.
  // Match ANY demo's seed marker — including runtime ad-hoc skins that aren't
  // in the module-load KNOWN_DEMOS snapshot — so switching back to an ad-hoc
  // demo still re-seeds. Covers the unversioned key and `sgDemo:<demo>:seeded:vN`.
  for (const key of Object.keys(localStorage)) {
    if (/^sgDemo:.+:seeded/.test(key)) {
      localStorage.removeItem(key);
    }
  }
}

// Re-export skin helpers so legacy callers can still grab them from
// './demo-flag' without learning about the new module split.
export {
  getSkin,
  listSkinNames,
  setSkinOverrides,
  setAdhocSkin,
  deleteAdhocSkin,
  isAdhocSkin,
  effectiveLenderType,
  getCode,
  getEntityLabel,
  getUserEmail,
} from './demo-skins';
export type { DemoBase, DemoCountryCode, DemoSkin, DemoSkinIdentity, DemoLenderType, SkinName } from './demo-skins';
export { DEMO_BASES } from './demo-skins';
